import { useMsal } from '@azure/msal-react';
import { WebAuth } from 'auth0-js';
import ls from 'localstorage-slim';
import _ from 'lodash';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { setUserInformation } from '../redux/auth/auth-slice';
import { isAuthenticated } from '../redux/auth/authUtils';
import { RootState } from '../redux/types';

const useUserInfo = (): void => {
  const dispatch = useDispatch();
  const auth = useSelector((state: RootState) => state.auth);
  const { instance } = useMsal();

  useEffect(() => {
    if (!isAuthenticated(auth)) return;

    // If MSAL has an active account, user info comes from B2C token claims
    const activeAccount = instance.getActiveAccount();
    if (activeAccount?.idTokenClaims) {
      const claims = activeAccount.idTokenClaims as Record<string, unknown>;
      dispatch(
        setUserInformation({
          firstName: (claims.given_name as string) || '',
          lastName: (claims.family_name as string) || '',
          emailVerified: true,
          email: (claims.username as string) || '',
        }),
      );
      return;
    }

    // Fallback: Auth0 userInfo for sign-up flow users
    const webAuth = new WebAuth({
      domain: import.meta.env.VITE_AUTH0_DOMAIN,
      clientID: import.meta.env.VITE_AUTH0_CLIENT_ID,
      redirectUri: `${window.location.origin}/callback`,
      audience: import.meta.env.VITE_AUTH0_AUDIENCE,
      responseType: 'id_token token',
    });
    webAuth.client.userInfo(auth.accessToken, (err, user) => {
      const nickname = user?.nickname.split('.');
      const firstName =
        user?.user_metadata?.given_name ||
        user?.given_name ||
        _.startCase(nickname[0]) ||
        ls.get('first_name') ||
        '';
      const lastName =
        user?.user_metadata?.family_name ||
        user?.family_name ||
        _.startCase(nickname[1]) ||
        ls.get('last_name') ||
        '';
      if (user) {
        dispatch(
          setUserInformation({
            firstName,
            lastName,
            emailVerified: user.email_verified || false,
            email: user.email || '',
          }),
        );
      }
    });
  }, [auth, instance, dispatch]);
};

export default useUserInfo;
